import React, { useEffect, useRef, useState } from "react";
import { track } from "@vercel/analytics";
import mixpanel from "../../mixpanel";
import "./YouthPage.css";

const TOTAL = 9;

export default function YouthPage() {
  const [current, setCurrent]   = useState(0);
  const [animated, setAnimated] = useState<Set<number>>(new Set([0]));
  const containerRef            = useRef<HTMLDivElement>(null);

  useEffect(() => {
    document.body.style.overflow = "hidden";
    document.documentElement.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
      document.documentElement.style.overflow = "";
    };
  }, []);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const onScroll = () => {
      const h = el.clientHeight;
      if (!h) return;
      const idx = Math.round(el.scrollTop / h);
      setCurrent(idx);
      setAnimated(prev => {
        if (prev.has(idx)) return prev;
        const next = new Set(prev);
        next.add(idx);
        return next;
      });
    };
    el.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => el.removeEventListener("scroll", onScroll);
  }, []);

  const scrollTo = (idx: number) => {
    const el = containerRef.current;
    if (!el) return;
    el.scrollTo({ top: idx * el.clientHeight, behavior: "smooth" });
  };

  const an = (i: number) => animated.has(i);

  return (
    <>
      <nav className="youth-dots" aria-label="Navigation">
        {Array.from({ length: TOTAL }).map((_, i) => (
          <button
            key={i}
            className={`yd-dot ${current === i ? "yd-dot-active" : ""}`}
            onClick={() => scrollTo(i)}
            aria-label={`Slide ${i + 1}`}
          />
        ))}
      </nav>

      <div className="youth-container" ref={containerRef}>

        {/* ══ 00 Hero ══ */}
        <section className="ys-section ys-section-hero">
          <div className={`ys-hero ${an(0) ? "ys-animate" : ""}`}>
            <span className="ys-step-bg">00</span>
            <div className="ys-hero-content">
              <span className="ys-hero-label">Կարիերա · 6 Քայլ</span>
              <h1 className="ys-hero-title">
                Առաջին<br />
                <span className="ys-hero-accent">աշխատանքդ</span><br />
                գտնելու համար
              </h1>
              <p className="ys-hero-sub">
                Միայն դիպլոմը բավարար չէ։ Քեզ պետք են գործնական հմտություններ,
                իրական փորձ, ճիշտ շրջապատ և համարձակություն՝ ինքդ քեզ ցույց տալու համար։
              </p>
              <button className="ys-hero-cta" onClick={() => scrollTo(1)}>
                Սկսենք →
              </button>
            </div>
            <div className="ys-hero-scroll" aria-hidden="true">
              <span>Scroll</span>
              <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
                <path d="M10 4v12M4 10l6 6 6-6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
          </div>
        </section>

        {/* ══ 01 English ══ */}
        <section className="ys-section ys-section-english">
          <div className={`ys-card ys-card-english ${an(1) ? "ys-animate" : ""}`}>
            <span className="ys-step-bg">01</span>
            <div className="ys-split">
              <div className="ys-split-left">
                <span className="ys-split-step ys-step-green">01 / 06</span>
                <h2 className="ys-split-title ys-title-green">Սովորիր<br />լեզուներ</h2>
                <div className="ys-split-ctas">
                  <a href="https://t.me/coffeecrossroad" target="_blank" rel="noopener noreferrer"
                     className="ys-cta-btn ys-cta-green"
                     onClick={() => { track("youth_coffeecrossroad_cta_click"); mixpanel.track("youth_coffeecrossroad_cta_clicked"); }}>
                    Coffee Crossroad →
                  </a>
                  <a href="https://www.youtube.com/@TED" target="_blank" rel="noopener noreferrer"
                     className="ys-cta-outline ys-outline-green"
                     onClick={() => { track("youth_ted_cta_click"); mixpanel.track("youth_ted_cta_clicked"); }}>
                    TED Talks →
                  </a>
                </div>
              </div>
              <div className="ys-split-right">
                <p className="ys-body ys-body-lead">Մասնավորապես՝ անգլերեն։</p>
                <p className="ys-body">Անգլերենը ոչ միայն բարձր վարձատրվող աշխատանքի համար է, այլ՝ այն բացում է գիտելիքի հսկայական պաշար՝ մասնագիտական նյութեր, գրքեր, նոր գաղափարներ, հնարավորություններ ու տեսակետներ, որոնք կարող են ամբողջությամբ փոխել մտածելակերպդ։</p>
                <p className="ys-body">Սկսիր YouTube-ից։ Դիտիր մարդկանց, ովքեր խոսում են կարիերայի, տեխնոլոգիաների և ինքնազարգացման մասին։ Սկզբում լսիր ենթագրերով, հետո փորձիր առանց դրանց։ Հաջորդ քայլը խոսելն է՝ գնա անգլերենի անվճար խոսակցական ակումբներ, դրանք կարող ես գտնել Coffee Crossroad-ի Telegram ալիքում։</p>
                <p className="ys-body ys-body-em">Սա գործնական ու լավագույն ներդրումներից մեկն է, որ կարող ես անել քեզ համար։</p>
              </div>
            </div>
          </div>
        </section>

        {/* ══ 02 Opportunities ══ */}
        <section className="ys-section ys-section-levelup">
          <div className={`ys-card ys-card-levelup ${an(2) ? "ys-animate" : ""}`}>
            <span className="ys-step-bg">02</span>
            <div className="ys-split">
              <div className="ys-split-left">
                <span className="ys-split-step ys-step-blue">02 / 06</span>
                <h2 className="ys-split-title ys-title-blue">Հետևիր<br />ճիշտ<br />ալիքներին</h2>
                <div className="ys-split-ctas">
                  <a href="https://t.me/LevelUpArmenians" target="_blank" rel="noopener noreferrer"
                     className="ys-cta-btn ys-cta-blue"
                     onClick={() => { track("youth_levelup_cta_click"); mixpanel.track("youth_levelup_cta_clicked"); }}>
                    LevelUp Armenians →
                  </a>
                </div>
              </div>
              <div className="ys-split-right">
                <p className="ys-body">Երբ անգլերենդ սկսի լավանալ, օգտագործիր այն՝ լսարանից ու համալսարանից դուրս հնարավորություններ գտնելու համար։</p>
                <p className="ys-body">Հետևիր LevelUp Armenians-ի նման ալիքների, որտեղ կարող ես գտնել ֆինանսավորվող ծրագրեր, դասընթացներ, միջազգային միջոցառումներ և երիտասարդների համար նախատեսված այլ հնարավորություններ։</p>
                <p className="ys-body">Դիմիր, նույնիսկ եթե վստահ չես, որ «բավականաչափ լավն ես»։ Գնա նոր տեղեր, ծանոթացիր մարդկանց հետ, բացահայտիր տարբեր ոլորտներ։ Տես՝ ուրիշներն ինչպես են մտածում ու աշխատում։</p>
                <p className="ys-body">Պարտադիր չէ, որ հիմա ամեն ինչ հստակ պատկերացնես։ Պարզապես սկսիր ներկայանալ ու մասնակցել։</p>
                <p className="ys-body ys-body-em">Ոչ մեկ չգիտի երբ է ամենալավ պահը, բոլորը մի կետից սկսում են, ամենալավ պահը գալիս է հետո։</p>
              </div>
            </div>
          </div>
        </section>

        {/* ══ 03 Jobs ══ */}
        <section className="ys-section ys-section-jobs">
          <div className={`ys-card ys-card-jobs ${an(3) ? "ys-animate" : ""}`}>
            <span className="ys-step-bg">03</span>
            <div className="ys-split">
              <div className="ys-split-left">
                <span className="ys-split-step ys-step-amber">03 / 06</span>
                <h2 className="ys-split-title ys-title-amber">Սկսիր<br />աշխատել</h2>
                <div className="ys-split-ctas">
                  <a href="https://hrdrone.am" target="_blank" rel="noopener noreferrer"
                     className="ys-cta-btn ys-cta-amber"
                     onClick={() => { track("youth_hrdrone_cta_click"); mixpanel.track("youth_hrdrone_cta_clicked"); }}>
                    hrdrone.am →
                  </a>
                  <a href="https://staff.am" target="_blank" rel="noopener noreferrer"
                     className="ys-cta-outline ys-outline-amber"
                     onClick={() => { track("youth_staffam_cta_click"); mixpanel.track("youth_staffam_cta_clicked"); }}>
                    staff.am →
                  </a>
                </div>
              </div>
              <div className="ys-split-right">
                <p className="ys-body">Առաջին աշխատանքդ, ամենայն հավանականությամբ, երազանքիդ աշխատանքը չի լինելու։ Եվ դա լրիվ նորմալ է։</p>
                <p className="ys-body">Դրա հիմնական նպատակը փորձ ձեռք բերելն է։ Կհասկանաս՝ ինչպես են աշխատում թիմերը, ինչպես է պետք շփվել աշխատանքային միջավայրում, ինչպես լուծել իրական խնդիրներ և, վերջապես, ինչով ես ուզում կամ չես ուզում զբաղվել։</p>
                <p className="ys-body">Օգտագործիր աշխատանքի հարթակները ու ծանոթությունների շրջանակը։ Դիմիր, նույնիսկ երբ դեռ ամբողջությամբ պատրաստ չես զգում։</p>
                <p className="ys-body ys-body-em">Ոչ մեկ չի սիրել իր առաջին աշխատանքը։ Բոլորը զգացել են, որ ոչինչ չգիտեն ու որ իրենց հանդեպ վատ են վարվում։</p>
              </div>
            </div>
          </div>
        </section>

        {/* ══ 04 LinkedIn ══ */}
        <section className="ys-section ys-section-linkedin">
          <div className={`ys-card ys-card-linkedin ${an(4) ? "ys-animate" : ""}`}>
            <span className="ys-step-bg">04</span>
            <div className="ys-split">
              <div className="ys-split-left">
                <span className="ys-split-step ys-step-cobalt">04 / 06</span>
                <h2 className="ys-split-title ys-title-cobalt">LinkedIn-ը՝<br />քո սիրելի<br />հարթակը</h2>
                <div className="ys-split-ctas">
                  <a href="https://www.linkedin.com" target="_blank" rel="noopener noreferrer"
                     className="ys-cta-btn ys-cta-cobalt"
                     onClick={() => { track("youth_linkedin_cta_click"); mixpanel.track("youth_linkedin_cta_clicked"); }}>
                    Բացել LinkedIn →
                  </a>
                </div>
              </div>
              <div className="ys-split-right">
                <p className="ys-body">LinkedIn-ում են բոլոր HR-ները, ընկերությունները, հիմնադիրները և տարբեր ոլորտների մասնագետները։ Քո գործն է այնպես անել, որ նրանք կարողանան գտնել քեզ։</p>
                <p className="ys-body">Տեղադրիր լուսանկար, մի քանի տողով ներկայացրու քեզ, նշիր կրթությունդ, հմտություններդ և ցույց տուր՝ ինչ ես սովորում կամ ստեղծում։</p>
                <p className="ys-body">Կապ հաստատիր HR մասնագետների հետ, նաև նրանց, ովքեր հեռավար աշխատանքի հնարավորություններ են առաջարկում։</p>
                <p className="ys-body ys-body-em">Կորցնելու գրեթե ոչինչ չունես, իսկ ձեռք բերել կարող ես մի ամբողջ կարիերա։</p>
              </div>
            </div>
          </div>
        </section>

        {/* ══ 05 Personal brand ══ */}
        <section className="ys-section ys-section-portfolio">
          <div className={`ys-card ys-card-portfolio ${an(5) ? "ys-animate" : ""}`}>
            <span className="ys-step-bg">05</span>
            <div className="ys-split">
              <div className="ys-split-left">
                <span className="ys-split-step ys-step-rose">05 / 06</span>
                <h2 className="ys-split-title ys-title-rose">Կառուցիր<br />անձնական<br />բրենդդ</h2>
                <div className="ys-split-ctas">
                  <a href="https://github.com" target="_blank" rel="noopener noreferrer"
                     className="ys-cta-btn ys-cta-rose"
                     onClick={() => { track("youth_github_cta_click"); mixpanel.track("youth_github_cta_clicked"); }}>
                    GitHub →
                  </a>
                  <a href="https://behance.net" target="_blank" rel="noopener noreferrer"
                     className="ys-cta-outline ys-outline-rose"
                     onClick={() => { track("youth_behance_cta_click"); mixpanel.track("youth_behance_cta_clicked"); }}>
                    Behance →
                  </a>
                </div>
              </div>
              <div className="ys-split-right">
                <p className="ys-body">Անձնական բրենդը պարզապես այն չէ, թե ինչ ես ասում քո մասին։ Դա ապացույցն է այն բանի, թե իրականում ինչ կարող ես անել։</p>
                <p className="ys-body">Եթե ծրագրավորող ես՝ GitHub-ը։ Եթե դիզայներ ես՝ Behance-ը։ LinkedIn-ը օգտակար է գրեթե բոլոր մասնագիտությունների համար, իսկ Instagram-ը՝ հատկապես տեսողական աշխատանքի համար։</p>
                <p className="ys-body">Հրապարակիր նախագծերդ, փորձարկումներդ, սովորածդ ու առաջընթացդ։ Պետք չէ փորձագետ լինել, իսկ աշխատանքներդ պարտադիր չէ, որ կատարյալ լինեն։</p>
                <p className="ys-body ys-body-em">Սկսիր փոքր քայլերից ու եղիր հետևողական։ Մեկ տարի անց շնորհակալ կլինես քեզ, որ սկսել ես հիմա։</p>
              </div>
            </div>
          </div>
        </section>

        {/* ══ 06 Community ══ */}
        <section className="ys-section ys-section-community">
          <div className={`ys-card ys-card-community ${an(6) ? "ys-animate" : ""}`}>
            <span className="ys-step-bg">06</span>
            <div className="ys-split">
              <div className="ys-split-left">
                <span className="ys-split-step ys-step-coral">06 / 06</span>
                <h2 className="ys-split-title ys-title-coral">Գտիր<br />քո<br />մարդկանց</h2>
                <div className="ys-split-ctas">
                  <a href="https://www.instagram.com/runandbrunch_yvn/" target="_blank" rel="noopener noreferrer"
                     className="ys-cta-btn ys-cta-coral"
                     onClick={() => { track("youth_runbrunch_cta_click"); mixpanel.track("youth_runbrunch_cta_clicked"); }}>
                    Run and Brunch →
                  </a>
                </div>
              </div>
              <div className="ys-split-right">
                <p className="ys-body">Կարիերան միայն համակարգչի առջև չի կառուցվում։ Հոբբիներդ կարող են բերել ընկերություններ, գաղափարներ ու հնարավորություններ, որոնք երբեք չէիր գտնի աշխատանքի կայքերում։</p>
                <p className="ys-body">Միացիր սպորտի, գրքերի, տեխնոլոգիաների կամ քեզ հետաքրքրող ցանկացած այլ թեմայի շուրջ ստեղծված համայնքներին։</p>
                <p className="ys-body">Տարբերակներից մեկը Run and Brunch-ն է — շաբաթ առավոտյան Երևանում 3 կմ վազք, հետո բրանչ ու նոր ծանոթություններ։ Այնտեղ կարող ես հանդիպել ակտիվ ու նպատակասլաց մարդկանց, ովքեր կարևորում են իրենց առողջությունը, աշխատանքն ու շրջապատը։ Հենց այսպիսի մարդկանց արժե ճանաչել։</p>
                <p className="ys-body ys-body-em">Գտիր այն համայնքները, որտեղ քեզ լավ ես զգում, ու շարունակիր մասնակցել։</p>
              </div>
            </div>
          </div>
        </section>

        {/* ══ 07 Bonus: Compound Interest ══ */}
        <section className="ys-section ys-section-bonus">
          <div className={`ys-card ys-card-bonus ${an(7) ? "ys-animate" : ""}`}>
            <div className="ys-bonus-inner">
              <span className="ys-bonus-label">Bonus</span>
              <h2 className="ys-bonus-title">
                Սովորիր<br />թե ինչ է<br />բարդ տոկոսը
              </h2>
              <p className="ys-bonus-sub">կտեվի 2 րոպե, կտա լավագույն արդյունքը քո կյանքում</p>
            </div>
          </div>
        </section>

        {/* ══ 08 Closing ══ */}
        <section className="ys-section ys-section-closing">
          <div className={`ys-card ys-card-closing ${an(8) ? "ys-animate" : ""}`}>
            <div className="ys-closing-inner">
              <span className="ys-closing-label">Հիշիր</span>
              <h2 className="ys-closing-title">
                Կարիերադ սկսվում է<br />
                առաջին աշխատանքից <em>առաջ</em>
              </h2>
              <p className="ys-closing-sub">
                Սկսելու համար քեզ պետք չի մեծ գումար, կատարյալ գնահատականներ կամ հստակ գծված պլան։
              </p>
              <div className="ys-closing-steps">
                {[
                  "Սովորիր լեզուներ",
                  "Հետևիր ճիշտ ալիքներին",
                  "Սկսիր աշխատել",
                  "LinkedIn-ը՝ քո սիրելի հարթակը",
                  "Կառուցիր անձնական բրենդդ",
                  "Գտիր քո մարդկանց",
                ].map((s, i) => (
                  <div key={i} className="ys-closing-step">
                    <span className="ys-closing-step-num">0{i + 1}</span>
                    <span className="ys-closing-step-text">{s}</span>
                  </div>
                ))}
              </div>
              <p className="ys-closing-final">
                Այս էջի բոլոր քայլերը կարող ես սկսել անվճար։<br />
                <strong>Առաջին քայլը պարզապես սկսելն է։</strong>
              </p>
            </div>
          </div>
        </section>

      </div>
    </>
  );
}
